import { useCallback, useEffect, useMemo, useState } from "react"
import { Badge, Box, Button, HStack, Input, Link, Text, VStack } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import DataTable from "../../../framework/DataTable"
import ErrorAlert from "../../../components/ErrorAlert"
import { apiFetch } from "../../../framework/api"
import { useAuthToken } from "../../../hooks/useAuthToken"
import { extractErrorInfo } from "../../../lib/api/error-utils"
import { formatDateTime } from "../../../utils/datetime"

type ResortOpeningsTabProps = {
  resortUid: string
}

type ResortOpeningRow = {
  featureUid: string
  featureName: string
  state: "available" | "pending" | "no_data" | "not_integrated"
  status?: string
  observedAt?: string
  source?: string
  externalName?: string
  sourceUrl?: string
}

type ResortOpeningsResponse = {
  rows: ResortOpeningRow[]
  refreshPending: boolean
}

function stateBadge(state: ResortOpeningRow["state"]) {
  switch (state) {
    case "available":
      return <Badge colorPalette="green">Available</Badge>
    case "pending":
      return <Badge colorPalette="yellow">Pending</Badge>
    case "no_data":
      return <Badge colorPalette="orange">No data</Badge>
    case "not_integrated":
      return <Badge colorPalette="gray">Not integrated</Badge>
    default:
      return <Badge colorPalette="gray">Unknown</Badge>
  }
}

function statusBadge(status?: string) {
  const normalized = (status ?? "").toLowerCase().trim()
  if (!normalized) return <Text color="gray.400">—</Text>
  if (normalized === "open") return <Badge colorPalette="green">Open</Badge>
  if (normalized === "closed") return <Badge colorPalette="red">Closed</Badge>
  return <Badge colorPalette="gray">{normalized}</Badge>
}

export default function ResortOpeningsTab({ resortUid }: ResortOpeningsTabProps) {
  const token = useAuthToken()
  const [filter, setFilter] = useState("")
  const [rows, setRows] = useState<ResortOpeningRow[]>([])
  const [refreshPending, setRefreshPending] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null)

  const loadOpenings = useCallback(
    async (refresh: boolean) => {
      if (!resortUid) return

      if (refresh) {
        setIsRefreshing(true)
      } else {
        setIsLoading(true)
      }
      setError(null)

      try {
        const params = new URLSearchParams()
        if (filter.trim()) {
          params.set("filter", filter.trim())
        }
        if (refresh) {
          params.set("refresh", "true")
        }

        const response = await apiFetch<ResortOpeningsResponse>(
          `/admin/resorts/${encodeURIComponent(resortUid)}/openings?${params.toString()}`,
          { token, method: "GET" }
        )

        setRows(response.rows ?? [])
        setRefreshPending(Boolean(response.refreshPending))
        if (refresh) {
          setLastRefreshAt(new Date().toISOString())
        }
      } catch (err) {
        setError(err)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    },
    [resortUid, filter, token]
  )

  useEffect(() => {
    void loadOpenings(false)
  }, [loadOpenings])

  const columns = useMemo<ColumnDef<ResortOpeningRow>[]>(
    () => [
      {
        accessorKey: "featureName",
        header: "Lift",
        id: "featureName",
        cell: ({ row }) => (
          <Link
            href={`/resorts/${resortUid}/features/${row.original.featureUid}`}
            color="blue.200"
            fontWeight="semibold"
            _hover={{ textDecoration: "underline" }}
          >
            {row.original.featureName}
          </Link>
        ),
      },
      {
        accessorKey: "state",
        header: "State",
        id: "state",
        cell: ({ row }) => stateBadge(row.original.state),
      },
      {
        accessorKey: "status",
        header: "Status",
        id: "status",
        cell: ({ row }) => statusBadge(row.original.status),
      },
      {
        accessorKey: "observedAt",
        header: "Observed",
        id: "observedAt",
        cell: ({ row }) => formatDateTime(row.original.observedAt),
      },
      {
        accessorKey: "source",
        header: "Source",
        id: "source",
        cell: ({ row }) => row.original.source ?? "—",
      },
      {
        accessorKey: "externalName",
        header: "External name",
        id: "externalName",
        cell: ({ row }) => row.original.externalName ?? "—",
      },
      {
        accessorKey: "sourceUrl",
        header: "Source URL",
        id: "sourceUrl",
        cell: ({ row }) =>
          row.original.sourceUrl ? (
            <Link href={row.original.sourceUrl} target="_blank" rel="noreferrer" color="blue.200">
              Link
            </Link>
          ) : (
            "—"
          ),
      },
    ],
    [resortUid]
  )

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <VStack align="stretch" gap="3">
      <HStack justify="space-between" align="center" flexWrap="wrap" gap="3">
        <Text fontWeight="semibold">Lift opening readings</Text>
        <HStack gap="2" flexWrap="wrap">
          <Input
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter lifts by name"
            bg="gray.700"
            borderColor="gray.600"
            borderRadius="md"
            px="3"
            py="2"
            fontSize="sm"
            type="text"
            width="260px"
          />
          <Button size="sm" colorPalette="gray" onClick={() => void loadOpenings(false)} disabled={isLoading || isRefreshing}>
            Reload
          </Button>
          <Button size="sm" colorPalette="blue" onClick={() => void loadOpenings(true)} loading={isRefreshing} disabled={isLoading || isRefreshing}>
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </HStack>
      </HStack>

      <HStack gap="3" flexWrap="wrap" color="gray.300" fontSize="sm">
        <Text>Lifts: {rows.length}</Text>
        <Text>Refresh pending: {refreshPending ? "Yes" : "No"}</Text>
        <Text>Last refresh: {formatDateTime(lastRefreshAt)}</Text>
      </HStack>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

      <Box overflowX="auto">
        <DataTable columns={columns} data={rows} isLoading={isLoading} emptyMessage="No lifts found for this resort." />
      </Box>
    </VStack>
  )
}
